"use client";

import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import {
    buildMonthGrid,
    isDateAvailable,
    CalendarDay
} from "./calendarHelpers";

// --- Types ---

interface CalendarPickerProps {
    availableDays: string[]; // e.g. ["Saturday","Sunday","Friday"]
    selectedDate: string; // YYYY-MM-DD or ""
    onDateSelect: (date: string) => void;
    disabled?: boolean;
    todayStr: string; // YYYY-MM-DD — passed in to avoid SSR mismatch
}

type DayVariant = "available" | "unavailable" | "selected" | "padding";

// --- Styled Components ---

const SCCalendarWrapper = styled.div<{ $disabled?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    ${({ $disabled }) =>
        $disabled &&
        css`
            opacity: 0.4;
            pointer-events: none;
        `}
`;

const SCCalendarHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 8px 0;
`;

const SCCalendarMonthLabel = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: #0c1a2e;
`;

const SCCalendarNav = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #0ea5e9;
    font-size: 18px;
    border-radius: 8px;
    padding: 4px 8px;
    line-height: 1;

    &:hover:not(:disabled) {
        background: #e0f2fe;
    }

    &:disabled {
        opacity: 0.3;
        cursor: default;
    }
`;

const SCWeekdayHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
`;

const SCWeekdayCell = styled.div`
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    color: #0c1a2e;
    padding: 4px;
`;

const SCCalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
`;

const dayVariantStyles: Record<DayVariant, ReturnType<typeof css>> = {
    available: css`
        background: white;
        cursor: pointer;
        color: #0c1a2e;

        &:hover {
            background: #e0f2fe;
        }
    `,
    unavailable: css`
        background: #e2e8f0;
        color: #94a3b8;
        opacity: 0.5;
        cursor: not-allowed;
    `,
    selected: css`
        background: #e0f2fe;
        border: 1.5px solid #0ea5e9;
        color: #0c1a2e;
        font-weight: 700;
        cursor: pointer;
    `,
    padding: css`
        background: transparent;
        cursor: default;
    `
};

const SCCalendarDay = styled.div<{ $variant: DayVariant }>`
    min-height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    ${({ $variant }) => dayVariantStyles[$variant]}
`;

// --- Weekday header labels ---

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// --- Helpers ---

function getDayVariant(
    day: CalendarDay,
    selectedDate: string,
    availableDays: string[],
    todayStr: string
): DayVariant {
    if (day.isPadding) {
        return "padding";
    }
    if (day.date === selectedDate) {
        return "selected";
    }
    if (isDateAvailable(day.date, availableDays, todayStr)) {
        return "available";
    }
    return "unavailable";
}

function getMaxMonth(todayStr: string): { year: number; month: number } {
    if (!todayStr) {
        const now = new Date();
        const max = new Date(now);
        max.setDate(max.getDate() + 60);
        return { year: max.getFullYear(), month: max.getMonth() };
    }
    const [y, m, d] = todayStr.split("-").map(Number);
    const todayDate = new Date(Date.UTC(y, m - 1, d));
    const maxDate = new Date(todayDate);
    maxDate.setDate(maxDate.getDate() + 60);
    return { year: maxDate.getUTCFullYear(), month: maxDate.getUTCMonth() };
}

function getTodayMonth(todayStr: string): { year: number; month: number } {
    if (!todayStr) {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    }
    const [y, m] = todayStr.split("-").map(Number);
    return { year: y, month: m - 1 };
}

// --- Component ---

export default function CalendarPicker({
    availableDays,
    selectedDate,
    onDateSelect,
    disabled = false,
    todayStr
}: CalendarPickerProps) {
    const [displayMonth, setDisplayMonth] = useState<{
        year: number;
        month: number;
    } | null>(null);

    // Initialize displayMonth on mount to avoid SSR hydration mismatch
    useEffect(() => {
        const todayMonth = getTodayMonth(todayStr);
        setDisplayMonth(todayMonth);
    }, [todayStr]);

    if (!displayMonth) {
        // Render nothing during SSR / before hydration
        return null;
    }

    const todayMonth = getTodayMonth(todayStr);
    const maxMonth = getMaxMonth(todayStr);

    const isPrevDisabled =
        displayMonth.year < todayMonth.year ||
        (displayMonth.year === todayMonth.year &&
            displayMonth.month <= todayMonth.month);

    const isNextDisabled =
        displayMonth.year > maxMonth.year ||
        (displayMonth.year === maxMonth.year &&
            displayMonth.month >= maxMonth.month);

    function handlePrev() {
        if (isPrevDisabled) return;
        setDisplayMonth((prev) => {
            if (!prev) return prev;
            if (prev.month === 0) {
                return { year: prev.year - 1, month: 11 };
            }
            return { year: prev.year, month: prev.month - 1 };
        });
    }

    function handleNext() {
        if (isNextDisabled) return;
        setDisplayMonth((prev) => {
            if (!prev) return prev;
            if (prev.month === 11) {
                return { year: prev.year + 1, month: 0 };
            }
            return { year: prev.year, month: prev.month + 1 };
        });
    }

    const grid = buildMonthGrid(displayMonth.year, displayMonth.month);

    return (
        <SCCalendarWrapper $disabled={disabled}>
            <SCCalendarHeader>
                <SCCalendarNav
                    onClick={handlePrev}
                    disabled={isPrevDisabled}
                    aria-label="Previous month"
                    type="button"
                >
                    &#8249;
                </SCCalendarNav>
                <SCCalendarMonthLabel>
                    {grid.monthName} {grid.year}
                </SCCalendarMonthLabel>
                <SCCalendarNav
                    onClick={handleNext}
                    disabled={isNextDisabled}
                    aria-label="Next month"
                    type="button"
                >
                    &#8250;
                </SCCalendarNav>
            </SCCalendarHeader>

            <SCWeekdayHeader>
                {WEEKDAY_LABELS.map((label) => (
                    <SCWeekdayCell key={label}>{label}</SCWeekdayCell>
                ))}
            </SCWeekdayHeader>

            <SCCalendarGrid>
                {grid.weeks.map((week, wi) =>
                    week.map((day, di) => {
                        const variant = getDayVariant(
                            day,
                            selectedDate,
                            availableDays,
                            todayStr
                        );
                        return (
                            <SCCalendarDay
                                key={`${wi}-${di}`}
                                $variant={variant}
                                onClick={() => {
                                    if (
                                        variant === "available" ||
                                        variant === "selected"
                                    ) {
                                        onDateSelect(day.date);
                                    }
                                }}
                                aria-label={
                                    day.isPadding
                                        ? undefined
                                        : `${day.dayName} ${day.dayOfMonth}`
                                }
                                aria-disabled={
                                    variant === "unavailable" ? true : undefined
                                }
                            >
                                {day.isPadding ? "" : day.dayOfMonth}
                            </SCCalendarDay>
                        );
                    })
                )}
            </SCCalendarGrid>
        </SCCalendarWrapper>
    );
}
